<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ContactMultiSelect from '../components/ContactMultiSelect.vue';
import ConflictDialog from '../components/ConflictDialog.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const businessAccount = ref(null);
const isLoading = ref(false);
const contacts = ref([]);

const dialogRef = ref(null);
const conflictDialogRef = ref(null);
const selectedContacts = ref([]);
const contactMultiSelectRef = ref(null);
const selectedContactIds = ref([]);
const conflicts = ref([]);
const pendingContactIds = ref([]);
const contactDecisions = ref({}); // { contactId: { businessAccountId: 'force' | 'keep_both' | 'skip' } }

const fetchBusinessAccount = async () => {
  isLoading.value = true;
  try {
    const businessAccountId = route.params.businessAccountId;
    const response = await store.dispatch(
      'businessAccounts/show',
      businessAccountId
    );
    businessAccount.value = response;
    contacts.value = response.contacts || [];
  } catch (error) {
    alert(
      getErrorMessage(
        error,
        t('BUSINESS_ACCOUNTS.DETAILS.ERROR.LOADING_ACCOUNT')
      )
    );
    businessAccount.value = null;
    contacts.value = [];
  } finally {
    isLoading.value = false;
  }
};

const goToAccountsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(accountScopedRoute('accounts_dashboard_index'));
  }
};

const openDialog = () => {
  dialogRef.value?.open();
  if (contactMultiSelectRef.value) {
    contactMultiSelectRef.value.loadInitialContacts();
  }
};

const closeDialog = () => {
  dialogRef.value?.close();
  resetForm();
};

const resetForm = () => {
  selectedContacts.value = [];
  selectedContactIds.value = [];
};

const getSelectedContacts = () => {
  return selectedContacts.value;
};

const getErrorMessage = (error, defaultMessage) => {
  const errorData = error?.response?.data;
  if (errorData?.error) return errorData.error;
  if (Array.isArray(errorData?.errors)) return errorData.errors.join(', ');
  if (errorData?.errors) return errorData.errors;
  return error?.message || defaultMessage;
};

const handleContactSelect = contact => {
  const index = selectedContacts.value.findIndex(c => c.id === contact.id);
  if (index > -1) {
    selectedContacts.value.splice(index, 1);
  } else {
    selectedContacts.value.push(contact);
  }
  selectedContactIds.value = selectedContacts.value.map(c => c.id);
};

const handleContactRemove = contact => {
  const index = selectedContacts.value.findIndex(c => c.id === contact.id);
  if (index > -1) {
    selectedContacts.value.splice(index, 1);
    selectedContactIds.value = selectedContacts.value.map(c => c.id);
  }
};

const linkContacts = async (force = false) => {
  try {
    if (selectedContactIds.value.length === 0) {
      return;
    }

    const response = await store.dispatch('businessAccounts/linkContacts', {
      id: businessAccount.value.id,
      contactIds: selectedContactIds.value,
      force: force,
    });

    // Check if there are conflicts
    if (response?.conflicts && response.conflicts.length > 0) {
      conflicts.value = response.conflicts;
      pendingContactIds.value = selectedContactIds.value;
      conflictDialogRef.value?.open();
      return;
    }

    closeDialog();
    await fetchBusinessAccount();
  } catch (error) {
    alert(
      getErrorMessage(
        error,
        t('BUSINESS_ACCOUNTS.DETAILS.ERROR.LINKING_CONTACTS')
      )
    );
  }
};

const handleApplyDecisions = async () => {
  try {
    const response = await store.dispatch('businessAccounts/linkContacts', {
      id: businessAccount.value.id,
      contactIds: pendingContactIds.value,
      contactDecisions: contactDecisions.value,
    });
    closeDialog();
    contactDecisions.value = {};
    await fetchBusinessAccount();
  } catch (error) {
    alert(
      getErrorMessage(
        error,
        t('BUSINESS_ACCOUNTS.DETAILS.ERROR.LINKING_CONTACTS')
      )
    );
  }
};

const handleCancelConflict = () => {
  conflicts.value = [];
  pendingContactIds.value = [];
  contactDecisions.value = {};
};

const unlinkContact = async contactId => {
  try {
    await store.dispatch('businessAccounts/unlinkContact', {
      id: businessAccount.value.id,
      contactId: contactId,
    });
    await fetchBusinessAccount();
  } catch (error) {
    alert(
      getErrorMessage(
        error,
        t('BUSINESS_ACCOUNTS.DETAILS.ERROR.UNLINKING_CONTACT')
      )
    );
  }
};

const showDeleteBusinessAccountModal = ref(false);
const loading = ref(false);

const openDeletePopup = () => {
  showDeleteBusinessAccountModal.value = true;
};

const closeDeleteBusinessAccountModal = () => {
  showDeleteBusinessAccountModal.value = false;
};

const deleteBusinessAccount = async () => {
  if (!businessAccount.value) return;

  loading.value = true;
  try {
    await store.dispatch('businessAccounts/delete', businessAccount.value.id);
    useAlert(t('CONTACTS_LAYOUT.DELETE.API.SUCCESS_MESSAGE'));
    router.push(accountScopedRoute('accounts_dashboard_index'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value = false;
  }
};

const confirmDeletion = () => {
  deleteBusinessAccount();
  closeDeleteBusinessAccountModal();
};

const deleteMessage = computed(() => {
  return businessAccount.value ? ` ${businessAccount.value.name}` : '';
});

onMounted(() => {
  fetchBusinessAccount();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <header class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div
        class="flex items-center justify-between w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
      >
        <div class="flex items-center gap-4">
          <button
            class="p-2 hover:bg-n-50 rounded-lg transition-colors"
            @click="goToAccountsList"
          >
            <span class="i-ph-arrow-left text-n-slate-11 size-5" />
          </button>
          <span class="text-xl font-medium truncate text-n-slate-12">
            {{ t('BUSINESS_ACCOUNTS.DETAILS.TITLE') }}
          </span>
        </div>
        <Button
          :label="t('BUSINESS_ACCOUNTS.DETAILS.DELETE_ACCOUNT')"
          color="ruby"
          :is-loading="loading"
          @click="openDeletePopup"
        />
      </div>
    </header>

    <main class="flex-1 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] px-6 py-6">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div v-else-if="businessAccount" class="flex flex-col gap-8">
          <div class="border border-n-weak rounded-lg p-6">
            <div class="flex items-start gap-6">
              <Avatar :name="businessAccount.name" :size="48" />
              <div class="flex flex-col gap-2 flex-1">
                <h1 class="text-2xl font-semibold text-n-slate-12">
                  {{ businessAccount.name }}
                </h1>
                <div class="flex flex-col gap-1">
                  <span
                    v-if="businessAccount.domain"
                    class="text-sm text-n-slate-11 flex items-center gap-1"
                  >
                    <span class="i-ph-globe text-n-slate-10 size-4 mr-1" />
                    {{ businessAccount.domain }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="border border-n-weak rounded-lg">
            <div
              class="p-6 border-b border-n-weak flex items-center justify-between"
            >
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ t('BUSINESS_ACCOUNTS.DETAILS.LINKED_CONTACTS') }} ({{
                  contacts.length
                }})
              </h2>
              <div class="text-right">
                <Button
                  :label="t('BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACT')"
                  color="blue"
                  @click="openDialog"
                />
              </div>
              <Dialog ref="dialogRef" width="2xl">
                <div class="flex flex-col gap-6">
                  <span class="py-1 text-sm font-medium text-n-slate-12">
                    {{
                      t('BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.TITLE')
                    }}
                  </span>
                  <div class="flex flex-col items-start gap-2">
                    <span class="py-1 text-sm font-medium text-n-slate-12">
                      {{
                        t(
                          'BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.LABEL'
                        )
                      }}
                    </span>
                    <ContactMultiSelect
                      ref="contactMultiSelectRef"
                      :selected-items="getSelectedContacts()"
                      :multiselector-title="
                        t(
                          'BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.MULTISELECTOR_TITLE'
                        )
                      "
                      :multiselector-placeholder="
                        t(
                          'BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.SEARCH_PLACEHOLDER'
                        )
                      "
                      :input-placeholder="
                        t(
                          'BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.INPUT_PLACEHOLDER'
                        )
                      "
                      icon="👥"
                      @select="handleContactSelect"
                      @remove="handleContactRemove"
                    />
                  </div>
                </div>
                <template #footer>
                  <div
                    class="flex items-center justify-between w-full gap-3 p-2"
                  >
                    <Button
                      :label="
                        t(
                          'BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.BUTTONS.CANCEL'
                        )
                      "
                      variant="link"
                      type="button"
                      class="h-10 hover:!no-underline hover:text-n-brand"
                      @click="closeDialog"
                    />
                    <Button
                      :label="
                        t(
                          'BUSINESS_ACCOUNTS.DETAILS.LINK_CONTACTS_DIALOG.BUTTONS.LINK'
                        )
                      "
                      color="blue"
                      :disabled="selectedContactIds.length === 0"
                      @click="linkContacts"
                    />
                  </div>
                </template>
              </Dialog>
            </div>

            <ConflictDialog
              ref="conflictDialogRef"
              :conflicts="conflicts"
              :contact-decisions="contactDecisions"
              @update:contact-decisions="contactDecisions = $event"
              @apply-decisions="handleApplyDecisions"
              @cancel="handleCancelConflict"
            />

            <div v-if="contacts.length > 0" class="p-6">
              <div class="grid gap-4">
                <div
                  v-for="contact in contacts"
                  :key="contact.id"
                  class="flex items-center gap-4 p-4 border border-n-weak rounded-lg hover:bg-n-50 transition-colors"
                >
                  <Avatar :name="contact.name" :size="40" />
                  <div class="flex flex-col gap-1 flex-1">
                    <h3 class="font-medium text-n-slate-12">
                      {{ contact.name }}
                    </h3>
                    <div class="flex flex-col gap-1">
                      <span
                        class="text-sm text-n-slate-11 flex items-center gap-1"
                      >
                        <span
                          class="i-ph-envelope text-n-slate-10 size-3 mr-1"
                        />
                        {{ contact.email }}
                      </span>
                      <span
                        class="text-sm text-n-slate-11 flex items-center gap-1"
                      >
                        <span class="i-ph-phone text-n-slate-10 size-3 mr-1" />
                        {{ contact.phone_number || '-' }}
                      </span>
                    </div>
                  </div>
                  <div class="text-right">
                    <Button
                      :label="t('BUSINESS_ACCOUNTS.DETAILS.UNLINK_CONTACT')"
                      color="ruby"
                      @click="unlinkContact(contact.id)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="p-6 text-center">
              <div
                class="w-12 h-12 bg-n-100 rounded-full flex items-center justify-center mx-auto mb-3"
              >
                <span class="i-ph-users text-n-400 size-6" />
              </div>
              <h3 class="text-base font-medium text-n-900 mb-1">
                {{ t('BUSINESS_ACCOUNTS.DETAILS.EMPTY_STATE.TITLE') }}
              </h3>
              <p class="text-sm text-n-600">
                {{ t('BUSINESS_ACCOUNTS.DETAILS.EMPTY_STATE.SUBTITLE') }}
              </p>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-12">
          <div
            class="w-16 h-16 bg-n-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <span class="i-ph-warning text-n-400 size-8" />
          </div>
          <h3 class="text-lg font-medium text-n-900 mb-2">
            {{ t('BUSINESS_ACCOUNTS.DETAILS.ERROR.TITLE') }}
          </h3>
          <p class="text-n-600 mb-4">
            {{ t('BUSINESS_ACCOUNTS.DETAILS.ERROR.MESSAGE') }}
          </p>
          <Button
            :label="t('BUSINESS_ACCOUNTS.DETAILS.ERROR.BACK_TO_LIST')"
            color="blue"
            @click="goToAccountsList"
          />
        </div>
      </div>
    </main>

    <woot-delete-modal
      v-model:show="showDeleteBusinessAccountModal"
      :on-close="closeDeleteBusinessAccountModal"
      :on-confirm="confirmDeletion"
      :title="t('CONTACTS_LAYOUT.DELETE.CONFIRM.TITLE')"
      :message="t('CONTACTS_LAYOUT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="t('CONTACTS_LAYOUT.DELETE.CONFIRM.YES')"
      :reject-text="t('CONTACTS_LAYOUT.DELETE.CONFIRM.NO')"
    />
  </div>
</template>
